import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cva, VariantProps } from 'class-variance-authority'
import { X } from 'lucide-react'
import * as React from 'react'
import { cn } from '@/lib/utils'
import { Button } from './button'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 backdrop-blur-xs data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const dialogContentVariants = cva(
  // `grid-cols-[minmax(0,1fr)]`: a plain `grid` sizes its column to the widest child's max-content,
  // so one wide child (a table, a long unbroken string) drags the whole panel past `max-w` and off
  // the viewport. `minmax(0,1fr)` lets the column shrink, leaving children to scroll or truncate.
  `relative z-50 grid grid-cols-[minmax(0,1fr)] w-full max-w-lg gap-4 border bg-layer-1 p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg`,
  {
    defaultVariants: {
      size: 'default',
    },
    variants: {
      size: {
        wide: 'max-w-2xl max-md:w-full max-md:max-w-full',
        // Width-only on purpose: pinning the panel to `h-screen` made it its own scroll container,
        // which shuts the overlay's scrolling off and strands anything past the fold. Let it grow
        // and the overlay scrolls it, per the pattern documented below.
        ultrawide: 'max-w-4xl max-md:w-full max-md:max-w-full max-xl:max-w-screen',
        default: '',
      },
    },
  },
)

type DialogAnchor = 'top' | 'center'

// Scrollable-overlay pattern (per Radix docs + shadcn issue #16):
// The Overlay is the scroll container — fixed inset-0 overflow-y-auto — and holds
// an in-flow wrapper that does the positioning. The wrapper is `min-h-full`, not
// `h-full`, so it grows past the viewport when the panel is taller: centering then
// has nothing to overflow, and the whole panel stays reachable by scrolling.
// Never position Content absolutely here — that takes it out of flow, so the
// overlay's scroll height ignores it and anything above the fold is unreachable.
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> &
    VariantProps<typeof dialogContentVariants> & {
      anchor?: DialogAnchor
      closeButtonVisible?: boolean
    } & {
      outerContainerClassName?: string
    }
>(
  (
    { className, children, size = 'wide', anchor = 'center', closeButtonVisible = true, ...props },
    ref,
  ) => (
    <DialogPortal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 overflow-y-auto bg-black/70 backdrop-blur-xs data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
        <div
          className={cn(
            'padding-container',
            'flex min-h-full flex-col items-center p-[3dvh]',
            anchor === 'center' ? 'justify-center' : 'justify-start',
            { 'max-xl:p-0': size === 'ultrawide' },
            props.outerContainerClassName,
          )}
        >
          <DialogPrimitive.Content
            ref={ref}
            className={cn(dialogContentVariants({ size }), className)}
            {...props}
          >
            {children}
            {closeButtonVisible && (
              <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                <Button aria-label="Close" variant="outline" size="icon">
                  <X />
                </Button>
              </DialogPrimitive.Close>
            )}
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Overlay>
    </DialogPortal>
  ),
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col [&+*]:my-6 space-y-4 text-center sm:text-left', className)}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-text-secondary', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

const DialogScrollable = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('max-h-[60vh] overflow-auto [&_form]:m-[1px]', className)}
    {...props}
  />
))
DialogScrollable.displayName = 'DialogScrollable'

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogScrollable,
  DialogTitle,
  DialogTrigger,
}
